import React, { useCallback } from 'react';
import { jsPDF } from 'jspdf';

const CandidatePoolGrid = () => {
  const primaryPool = [
    'Current direct reports for the target role',
    'Peers with relevant experience who could lateral into the role',
    'High-performing managers in related functional areas',
    'High-performing managers even if currently in a differing career path',
    'Previous role holders who could return in interim or permanent capacity'
  ];

  const secondaryPool = [
    'Emerging leaders showing advancement potential',
    'External candidates with credit union experience',
    'Internal candidates from other departments showing cross-functional capability',
    'Recent hires with relevant industry experience'
  ];

  const culturalFit = [
    {
      level: 'Strong Fit',
      description: 'Clearly understands and embraces cooperative principles',
      color: 'bg-green-50 border-green-200'
    },
    {
      level: 'Moderate Fit',
      description: 'Generally aligned but may need coaching on specific aspects',
      color: 'bg-yellow-50 border-yellow-200'
    },
    {
      level: 'Poor Fit',
      description: "Doesn't understand or struggles with cooperative culture",
      color: 'bg-red-50 border-red-200'
    }
  ];

  // Generate PDF
  const generatePDF = useCallback(() => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    let yPos = 20;

    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text('INITIAL CANDIDATE POOL DEVELOPMENT', pageWidth / 2, yPos, { align: 'center' });
    yPos += 5;

    // Underline
    doc.setDrawColor(50, 50, 50);
    doc.setLineWidth(0.5);
    doc.line(margin + 20, yPos, pageWidth - margin - 20, yPos);
    yPos += 15;

    // Two column layout
    const colWidth = (contentWidth - 10) / 2;

    // Primary Pool Header
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, yPos, colWidth, 10, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text('PRIMARY POOL', margin + colWidth / 2, yPos + 7, { align: 'center' });

    // Secondary Pool Header
    doc.rect(margin + colWidth + 10, yPos, colWidth, 10, 'F');
    doc.text('SECONDARY POOL', margin + colWidth + 10 + colWidth / 2, yPos + 7, { align: 'center' });
    yPos += 15;

    // Primary Pool Items
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(50, 50, 50);

    let primaryY = yPos;
    primaryPool.forEach((item) => {
      const lines = doc.splitTextToSize(`• ${item}`, colWidth - 10);
      doc.text(lines, margin + 5, primaryY);
      primaryY += lines.length * 5 + 3;
    });

    // Secondary Pool Items
    let secondaryY = yPos;
    secondaryPool.forEach((item) => {
      const lines = doc.splitTextToSize(`• ${item}`, colWidth - 10);
      doc.text(lines, margin + colWidth + 15, secondaryY);
      secondaryY += lines.length * 5 + 3;
    });

    yPos = Math.max(primaryY, secondaryY) + 10;

    // Cultural Fit Section
    doc.setDrawColor(50, 50, 50);
    doc.setLineWidth(0.3);
    doc.rect(margin, yPos, contentWidth, 70);

    // Cultural Fit Header
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, yPos, contentWidth, 10, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text('CULTURAL FIT SCREENING', pageWidth / 2, yPos + 7, { align: 'center' });
    yPos += 15;

    // Cultural Fit Grid
    const fitColWidth = contentWidth / 3;

    // Headers
    doc.setFillColor(248, 248, 248);
    culturalFit.forEach((fit, i) => {
      doc.rect(margin + (i * fitColWidth), yPos, fitColWidth, 8, 'F');
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(fit.level.toUpperCase(), margin + (i * fitColWidth) + fitColWidth / 2, yPos + 6, { align: 'center' });
    });
    yPos += 12;

    // Descriptions
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    culturalFit.forEach((fit, i) => {
      const lines = doc.splitTextToSize(fit.description, fitColWidth - 10);
      doc.text(lines, margin + (i * fitColWidth) + 5, yPos);
    });
    yPos += 20;

    // Decision Box
    doc.setFillColor(50, 50, 50);
    doc.rect(margin + 10, yPos, contentWidth - 20, 12, 'F');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('Only candidates with STRONG or MODERATE cultural fit advance to next evaluation step', pageWidth / 2, yPos + 8, { align: 'center' });

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text('Generated by SuccessionNow - The NEXT Method', pageWidth / 2, 285, { align: 'center' });

    // Save
    const fileName = `candidate-pool-framework-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  }, []);

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white px-8 py-8 text-center">
        <h2 className="text-xl md:text-2xl font-bold tracking-wide">INITIAL CANDIDATE POOL DEVELOPMENT</h2>
        <p className="text-gray-300 mt-2 text-sm">Succession Planning Candidate Framework</p>
      </div>

      <div className="p-6 md:p-8">
        {/* Two Column Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Primary Pool */}
          <div className="border-2 border-gray-300 rounded-lg overflow-hidden">
            <div className="bg-gray-100 px-4 py-3 border-b border-gray-300">
              <h3 className="font-bold text-gray-800 text-center">PRIMARY POOL</h3>
            </div>
            <div className="p-4">
              <ul className="space-y-3">
                {primaryPool.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-700">
                    <span className="text-gray-400 mt-1">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Secondary Pool */}
          <div className="border-2 border-gray-300 rounded-lg overflow-hidden">
            <div className="bg-gray-100 px-4 py-3 border-b border-gray-300">
              <h3 className="font-bold text-gray-800 text-center">SECONDARY POOL</h3>
            </div>
            <div className="p-4">
              <ul className="space-y-3">
                {secondaryPool.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-700">
                    <span className="text-gray-400 mt-1">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Cultural Fit Section */}
        <div className="border-2 border-gray-300 rounded-lg overflow-hidden mb-8">
          <div className="bg-gray-100 px-4 py-3 border-b border-gray-300">
            <h3 className="font-bold text-gray-800 text-center">CULTURAL FIT SCREENING</h3>
          </div>

          <div className="p-4">
            {/* Fit Grid */}
            <div className="grid md:grid-cols-3 gap-4 mb-4">
              {culturalFit.map((fit, i) => (
                <div key={i} className={`border rounded-lg p-4 text-center ${fit.color}`}>
                  <h4 className="font-bold text-gray-800 mb-2">{fit.level}</h4>
                  <p className="text-sm text-gray-600">{fit.description}</p>
                </div>
              ))}
            </div>

            {/* Decision Box */}
            <div className="bg-gray-800 text-white p-4 rounded-lg text-center">
              <p className="font-semibold flex items-center justify-center gap-2">
                <span className="text-lg">➤</span>
                Only candidates with <span className="text-green-400">STRONG</span> or <span className="text-yellow-400">MODERATE</span> cultural fit advance to next evaluation step
              </p>
            </div>
          </div>
        </div>

        {/* Download Button */}
        <div className="text-center pt-4 border-t border-gray-200">
          <button
            onClick={generatePDF}
            className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-8 py-3 rounded-full font-semibold shadow hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
          >
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default CandidatePoolGrid;
